import React, { ChangeEvent, useMemo, useState } from 'react';
import * as R from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
  Box,
  Checkbox,
  FormControlLabel,
  makeStyles,
  MenuItem,
  TextField,
} from '@material-ui/core';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import { PosPlugin } from 'plugins/plugin';
import { addSuccess } from 'actions/Error';
import { getPluginConfiguration } from 'reducers/Plugins';
import { getUserLoggedIn } from 'reducers/Login';
import { getProductsInShoppingCart, getTotal } from 'reducers/ShoppingCart';
import {
  getCurrentSalesDocPayments,
  getIsCurrentSaleAReturn,
  getReturnTotal,
} from 'reducers/sales';
import { getSetting } from 'reducers/configs/settings';
import SaveButton from 'components/CustomButtons/SaveButton';
import { RootState } from 'reducers';
import useProducts from 'utils/hooks/useProducts';
import { getIsSerialGiftcardDisabled } from 'reducers/Payments';

import { Conf } from '../types';
import { pluginID } from '../constants';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { checkIfGivexIsAllowedBasedOnAp } from '../utils';

export const getGivexConfiguration = state =>
  getPluginConfiguration<Conf>(pluginID)(state);

/** Card inputs also ask for pin (otherwise only has input for card number) */
export const getIsPinRequired = createSelector(
  getGivexConfiguration,
  ({ isPinRequired }) => isPinRequired,
);
/** Show 'Givex transfer' button in POS main grid */
export const getIsTransferRequired = createSelector(
  getGivexConfiguration,
  ({ isTransferEnabled }) => isTransferEnabled,
);

/** Current user is a givex admin (based on configured admin group ID) */
export const getisGivexAdmin = createSelector(
  getGivexConfiguration,
  state => getUserLoggedIn(state),
  (conf, { groupID }) => String(conf?.adminId) === String(groupID),
);
export const defaultGivexUrl = 'wss://localhost.erply.com:5658';
/** URL of the givex microservice */
export const getGivexURL = createSelector(
  getGivexConfiguration,
  conf => conf.givexUrl ?? defaultGivexUrl,
);
export const getHasGivexProductInCart = createSelector(
  getProductsInShoppingCart,
  getGivexConfiguration,
  (cart, conf) =>
    cart.some(p => Number(p.productID) === Number(conf.giftcardProductID)),
);

/**
 * If changed, the logic should also be changed in {@link checkIfGivexIsAllowedBasedOnAp }
 */
export const getIsAllowed = createSelector(
  getIsCurrentSaleAReturn,
  getReturnTotal,
  getTotal,
  getSetting('pos_allow_return_receipt_original_tender_only'),
  getCurrentSalesDocPayments,
  getGivexConfiguration,
  getIsSerialGiftcardDisabled,
  (
    isAReturn,
    returnTotal,
    total,
    originalTenderOnly,
    currentSalesDocPayments,
    conf,
    serialGiftCardDisabled,
  ) => {
    const shoppingCartTotal = isAReturn ? returnTotal : total;
    if (isAReturn) {
      if (originalTenderOnly) {
        return currentSalesDocPayments.some(
          originalPayment => originalPayment.cardType === 'GIVEX',
        );
      }
      // Check both the plugin config and the POS config - if either blocks, then block
      return conf.isAllowedOnReturnWithReceipt && !serialGiftCardDisabled;
    }
    if (shoppingCartTotal < 0) {
      return conf.isAllowedOnReturnWithoutReceipt;
    }
    return conf.isAllowedOnSale;
  },
);

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    '& .MuiTextField-root': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    '& .MuiFormControlLabel-root': {
      display: 'block',
    },
  },
}));

export const ComponentConfiguration: Required<
  PosPlugin<Conf>
>['ComponentConfigurationByLevel']['Company'] = ({ save }) => {
  const styles = useStyles();
  const dispatch: ThunkDispatch<RootState, unknown, Action> = useDispatch();

  const originalTendersOnly = useSelector(
    getSetting('pos_allow_return_receipt_original_tender_only'),
  );

  const currentConfig = useSelector(getGivexConfiguration);
  const [config, setConfig] = useState<Conf>(currentConfig);

  const cart = useSelector(getProductsInShoppingCart);

  const selectedProductID = Number(config.giftcardProductID);
  const currentProductID = Number(currentConfig.giftcardProductID);

  const {
    productsDict: {
      [selectedProductID]: selectedGiftCardProduct,
      [currentProductID]: savedGiftCardProduct,
    },
  } = useProducts({
    productIDs: [selectedProductID, currentProductID],
  });

  const productOptions = useMemo(
    () =>
      R.pipe(
        R.reject(R.isNil),
        R.uniqBy(R.prop('productID')),
      )([savedGiftCardProduct, selectedGiftCardProduct, ...cart]),
    [savedGiftCardProduct, selectedGiftCardProduct, cart],
  );

  const saveConfig = () => {
    save(
      R.evolve({
        givexUrl: a => a || undefined,
      })(config),
    );
    dispatch(addSuccess('Configuration saved'));
  };

  const onChangeProp = (prop: keyof Conf) => (
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const { type, value, checked } = e.target;
    const parsedValue = type === 'checkbox' ? checked : value;
    setConfig(R.assoc(prop, parsedValue));
  };

  return (
    <div className={styles.root}>
      <h5>Givex MS</h5>
      <TextField
        fullWidth
        variant="outlined"
        label="Givex Microservice"
        placeholder={defaultGivexUrl}
        type="text"
        onChange={onChangeProp('givexUrl')}
        value={config.givexUrl}
      />
      <TextField
        fullWidth
        variant="outlined"
        label="Admin id"
        type="text"
        onChange={onChangeProp('adminId')}
        value={config.adminId}
      />
      <TextField
        fullWidth
        variant="outlined"
        label="Default card number"
        type="text"
        onChange={onChangeProp('defaultCardNumber')}
        value={config.defaultCardNumber}
      />
      <TextField
        fullWidth
        variant="outlined"
        label="Default pin"
        type="text"
        onChange={onChangeProp('defaultPin')}
        value={config.defaultPin}
      />
      <FormControlLabel
        label="Use pin"
        control={
          <Checkbox
            onChange={onChangeProp('isPinRequired')}
            checked={config.isPinRequired}
          />
        }
      />
      <FormControlLabel
        label="Enable transfer"
        control={
          <Checkbox
            onChange={onChangeProp('isTransferEnabled')}
            checked={config.isTransferEnabled}
          />
        }
      />
      <FormControlLabel
        label="Use visual input field"
        control={
          <Checkbox
            onChange={onChangeProp('useFancyInput')}
            checked={config.useFancyInput}
          />
        }
      />
      <TextField
        fullWidth
        variant="outlined"
        label="Product to use as givex increment"
        select
        value={config.giftcardProductID ?? '-1'}
        onChange={onChangeProp('giftcardProductID')}
      >
        <MenuItem key="-1" value="-1">
          {cart.length === 0
            ? 'Add the product to cart first'
            : 'Select product...'}
        </MenuItem>
        {productOptions.map(o => (
          <MenuItem key={o.productID} value={o.productID}>
            {o.name}
          </MenuItem>
        ))}
      </TextField>
      <h5 className="mt-3">Allow Givex tender:</h5>
      <FormControlLabel
        label="Allow on sale"
        control={
          <Checkbox
            onChange={onChangeProp('isAllowedOnSale')}
            checked={config.isAllowedOnSale}
          />
        }
      />
      <FormControlLabel
        label={
          <>
            <span>Allow on return with receipt</span>
            {originalTendersOnly ? (
              <Box color="orange" paddingLeft="0.75rem">
                Overridden by <b>Only original tender types are allowed</b>{' '}
                setting (Payment Configuration)
              </Box>
            ) : null}
          </>
        }
        control={
          <Checkbox
            disabled={!!originalTendersOnly}
            onChange={onChangeProp('isAllowedOnReturnWithReceipt')}
            checked={config.isAllowedOnReturnWithReceipt}
          />
        }
      />
      <FormControlLabel
        label="Allow on return without receipt"
        control={
          <Checkbox
            onChange={onChangeProp('isAllowedOnReturnWithoutReceipt')}
            checked={config.isAllowedOnReturnWithoutReceipt}
          />
        }
      />
      <SaveButton action={saveConfig} title="Save" />
    </div>
  );
};
